import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Header } from '@components/Header';
import Done from '@components/icons/Done.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, GetAttorneyUsersResponseUserDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { attorneysKeys, TAttorneysListParams } from 'app/queries/attorneys/attorneysKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo } from 'react';
import { fetchAllAttorneyUsersForExport } from '../../api/fetchAllAttorneyUsersForExport';
import { createFetchAttorneyUsersQueryFn } from '../../api/fetchAttorneyUsers';
import { downloadAttorneyUsersCsv } from '../../helpers/downloadAttorneyUsersCsv';
import { Tabs } from '../Tabs';
import { CellContent } from './styles/CellContent';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize, toggleSort } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const sortingField = orderBy;
  const sortingReverse = orderType === 'desc';
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TAttorneysListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const usersQuery = useQuery({
    queryKey: attorneysKeys.userList(listParams),
    queryFn: createFetchAttorneyUsersQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const verifyMutation = useMutation({
    mutationFn: (email: string) => call(() => AdminService.verifyAttorney({ email })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: attorneysKeys.all }),
  });

  const deleteMutation = useMutation({
    mutationFn: (email: string) => call(() => AdminService.deleteAttorneyUser({ email })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: attorneysKeys.all }),
  });

  const exportMutation = useMutation({
    mutationFn: () => fetchAllAttorneyUsersForExport(call, { orderBy, orderType }),
    onSuccess: ({ data }) => {
      void downloadAttorneyUsersCsv(data);
    },
  });

  const verify = (email: string) => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Confirm',
      description: `Do you really want to verify this user (${email})?`,
      buttonText: 'Verify',
      onSuccess: () => {
        closeModal();
        verifyMutation.mutate(email);
      },
    });
  };

  const deleteAttorney = (email: string) => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Confirm',
      description: `Do you really want to delete this user (${email})?`,
      buttonText: 'Delete',
      onSuccess: () => {
        closeModal();
        deleteMutation.mutate(email);
      },
    });
  };

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const attorneyUsersList = usersQuery.data ?? { data: [], count: 0 };
  const isLoading = usersQuery.isFetching || isSearchSettling || verifyMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <Header
        title="Attorney Users"
        setSearch={setSearch}
        search={search}
        leftSideContent={[<Tabs key="PageTabs" currentTab="Users" />]}
        buttonLabel={attorneyUsersList.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={attorneyUsersList.data?.length ? exportCSV : undefined}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Email</Th>
                <Th>Verified</Th>
                <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
                  Created
                  {sortingField === 'createdAt' && <SortingDirection reverse={sortingReverse} />}
                </SortableTh>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {attorneyUsersList.data?.map((data: GetAttorneyUsersResponseUserDto) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>{data.id}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.email}</CellContent>
                  </Td>
                  <Td>
                    <CellContent className="text-center">
                      {data.emailVerified ? (
                        <Done />
                      ) : (
                        <OutlinedButton className="verify-button" onClick={() => verify(data.email)}>
                          Verify
                        </OutlinedButton>
                      )}
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                  </Td>
                  <Td>
                    <IconButtonDelete
                      onClick={() => {
                        deleteAttorney(data.email);
                      }}
                    >
                      <TrashBin />
                    </IconButtonDelete>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={attorneyUsersList?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
